import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, onSnapshot, doc, updateDoc, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { EyeIcon, CheckCircleIcon, XCircleIcon } from '../../components/icons/Icons';
import ExpenseDetailModal from './ExpenseDetailModal';

// Modal for confirming rejection
const RejectModal = ({ item, onClose, onReject }) => {
    const [reason, setReason] = useState('');
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
                <h2 className="text-xl font-bold text-red-600 mb-4">Reject Expense Report</h2>
                <p className="text-gray-600 mb-4">Please provide a reason for rejecting report: <strong>{item.reportNumber}</strong>.</p>
                <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows="4" className="w-full p-2 border border-gray-300 rounded-md" placeholder="Rejection comments..."></textarea>
                <div className="flex justify-end space-x-4 mt-6">
                    <button onClick={onClose} className="px-6 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300">Cancel</button>
                    <button onClick={() => onReject(item, 'Rejected', reason)} disabled={!reason.trim()} className="px-6 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 disabled:bg-red-300">Confirm Rejection</button>
                </div>
            </div>
        </div>
    );
};

// This component handles the entire approval workflow for managers.
export default function ExpenseApprovalCenter({ userProfile, showModal, appId }) {
    const [pendingReports, setPendingReports] = useState([]);
    const [pastReports, setPastReports] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedReport, setSelectedReport] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('pending');
    
    // State for past tab functionality
    const [sortConfig, setSortConfig] = useState({ key: 'updatedAt', direction: 'descending' });
    const [pastSearchTerm, setPastSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    // Fetch reports assigned to the current manager
    useEffect(() => {
        if (!userProfile) {
            setIsLoading(false);
            return;
        }

        const reportsRef = collection(db, `/artifacts/${appId}/public/data/expenseReports`);
        const q = query(reportsRef, where("managerId", "==", userProfile.uid));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const allReports = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            
            const pending = allReports.filter(r => r.status === 'Pending Approval');
            const past = allReports.filter(r => r.status !== 'Pending Approval' && r.status !== 'Draft');
            
            setPendingReports(pending);
            setPastReports(past);
            setIsLoading(false);
        }, (error) => {
            console.error("Error fetching expense reports:", error);
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [userProfile, appId]);

    // Handler for approving or rejecting a report
    const handleAction = async (report, newStatus, comments = "") => {
        const reportRef = doc(db, `/artifacts/${appId}/public/data/expenseReports`, report.id);
        try {
            await updateDoc(reportRef, {
                status: newStatus,
                approvalComments: comments,
                approvedBy: userProfile.name,
                updatedAt: new Date()
            });
            
            /*
            const updatedReportData = { ...report, status: newStatus, approvalComments: comments };
            
            const requesterRef = doc(db, `/artifacts/${appId}/users`, report.requesterId);
            const requesterSnap = await getDoc(requesterRef);
            if (requesterSnap.exists()) {
                const emailContent = expenseStatusUpdateTemplate(updatedReportData, newStatus, comments);
                await sendNotificationEmail({ to: [requesterSnap.data().email], ...emailContent, appId });
            }

            if (newStatus === 'Approved') {
                const financeEmail = await getFinanceEmailByEntity(report.entity);
                if (financeEmail) {
                    const emailContent = expenseApprovedForFinanceTemplate(updatedReportData, userProfile.name);
                    await sendNotificationEmail({ to: [financeEmail], ...emailContent, appId });
                }
            }
            */
            if (isRejectModalOpen) setIsRejectModalOpen(false);
            showModal("Success", `Report has been ${newStatus.toLowerCase()}.`);

        } catch (error) {
            console.error("Error updating report status:", error);
            showModal("Error", "Failed to update the report status.");
        }
    };

    const handleViewDetails = (report) => {
        setSelectedReport(report);
        setIsModalOpen(true);
    };

    const handleOpenRejectModal = (report) => {
        setSelectedReport(report);
        setIsRejectModalOpen(true);
    };

    // Memoized logic for past tab (search, sort, paginate)
    const processedPastReports = useMemo(() => {
        let filtered = [...pastReports];
        if (pastSearchTerm) {
            const lowercasedTerm = pastSearchTerm.toLowerCase();
            filtered = filtered.filter(r => 
                r.reportName.toLowerCase().includes(lowercasedTerm) || 
                r.requesterName.toLowerCase().includes(lowercasedTerm) ||
                r.reportNumber?.toLowerCase().includes(lowercasedTerm)
            );
        }
        filtered.sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];
            if (aValue?.toDate) aValue = aValue.toDate();
            if (bValue?.toDate) bValue = bValue.toDate();
            if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
            return 0;
        });
        return filtered;
    }, [pastReports, pastSearchTerm, sortConfig]);

    const paginatedPastReports = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return processedPastReports.slice(startIndex, startIndex + itemsPerPage);
    }, [processedPastReports, currentPage, itemsPerPage]);

    const totalPages = Math.ceil(processedPastReports.length / itemsPerPage);

    if (isLoading) return <LoadingSpinner />;

    return (
        <>
            {isModalOpen && <ExpenseDetailModal report={selectedReport} onClose={() => setIsModalOpen(false)} appId={appId} />}
            {isRejectModalOpen && <RejectModal item={selectedReport} onClose={() => setIsRejectModalOpen(false)} onReject={handleAction} />}

            <div>
                <h2 className="text-2xl font-semibold text-[#213368] mb-4">Expense Approval Center</h2>
                <div className="border-b border-gray-200 mb-4">
                    <nav className="-mb-px flex space-x-8">
                        <button onClick={() => setActiveTab('pending')} className={`${activeTab === 'pending' ? 'border-[#20BEC6] text-[#20BEC6]' : 'border-transparent text-gray-500'} py-4 px-1 border-b-2 font-medium text-sm`}>
                            Pending ({pendingReports.length})
                        </button>
                        <button onClick={() => setActiveTab('past')} className={`${activeTab === 'past' ? 'border-[#20BEC6] text-[#20BEC6]' : 'border-transparent text-gray-500'} py-4 px-1 border-b-2 font-medium text-sm`}>
                            Past ({pastReports.length})
                        </button>
                    </nav>
                </div>

                {activeTab === 'pending' && <ApprovalTable items={pendingReports} onView={handleViewDetails} onApprove={(item) => handleAction(item, 'Approved')} onReject={handleOpenRejectModal} />}
                
                {activeTab === 'past' && (
                    <PastTable 
                        items={paginatedPastReports} 
                        onView={handleViewDetails} 
                        sortConfig={sortConfig}
                        requestSort={(key) => setSortConfig(prev => ({ key, direction: prev.key === key && prev.direction === 'ascending' ? 'descending' : 'ascending' }))}
                        searchTerm={pastSearchTerm}
                        setSearchTerm={setPastSearchTerm}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        handlePageChange={(page) => setCurrentPage(page)}
                        itemsPerPage={itemsPerPage}
                        setItemsPerPage={(size) => { setItemsPerPage(size); setCurrentPage(1); }}
                    />
                )}
            </div>
        </>
    );
}

// Table for Pending items
const ApprovalTable = ({ items, onView, onApprove, onReject }) => (
    <div className="bg-white shadow-md rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
                <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Report ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted By</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Report Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date Submitted</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
                {items.length > 0 ? items.map(item => (
                    <tr key={item.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-600">{item.reportNumber}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{item.requesterName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{item.reportName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{item.createdAt?.toDate().toLocaleDateString()}</td>
                        {/* --- UPDATED: Display reimbursement currency --- */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{`${item.reimbursementCurrency} ${item.totalAmount.toFixed(2)}`}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div className="flex items-center space-x-3">
                                <button onClick={() => onView(item)} className="p-2 text-gray-500 hover:text-blue-600" title="View Details"><EyeIcon /></button>
                                <button onClick={() => onApprove(item)} className="p-2 text-green-500 hover:text-green-700" title="Approve"><CheckCircleIcon /></button>
                                <button onClick={() => onReject(item)} className="p-2 text-red-500 hover:text-red-700" title="Reject"><XCircleIcon /></button>
                            </div>
                        </td>
                    </tr>
                )) : (
                    <tr><td colSpan="6" className="text-center py-10 text-gray-500">No pending reports.</td></tr>
                )}
            </tbody>
        </table>
    </div>
);

// Table for Past items
const PastTable = ({ items, onView, sortConfig, requestSort, searchTerm, setSearchTerm, currentPage, totalPages, handlePageChange, itemsPerPage, setItemsPerPage }) => {
     const getStatusBadgeColor = (status) => {
        switch (status) {
            case 'Approved': return 'bg-green-100 text-green-800';
            case 'Rejected': return 'bg-red-100 text-red-800';
            case 'Reimbursed': return 'bg-blue-100 text-blue-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };
    return (
        <div>
            <div className="mb-4">
                <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search by report ID, name, or submitter..." className="w-full p-2 border rounded-lg"/>
            </div>
            <div className="bg-white shadow-md rounded-lg overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th onClick={() => requestSort('reportNumber')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Report ID</th>
                            <th onClick={() => requestSort('requesterName')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Submitted By</th>
                            <th onClick={() => requestSort('reportName')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Report Name</th>
                            <th onClick={() => requestSort('totalAmount')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Amount</th>
                            <th onClick={() => requestSort('updatedAt')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Action Date</th>
                            <th onClick={() => requestSort('status')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase"></th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                         {items.length > 0 ? items.map(item => (
                            <tr key={item.id}>
                                <td className="px-6 py-4 whitespace-nowrap font-mono text-sm">{item.reportNumber}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">{item.requesterName}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">{item.reportName}</td>
                                {/* --- UPDATED: Display reimbursement currency --- */}
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{`${item.reimbursementCurrency} ${item.totalAmount.toFixed(2)}`}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">{item.updatedAt?.toDate().toLocaleDateString()}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm"><span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeColor(item.status)}`}>{item.status}</span></td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm"><button onClick={() => onView(item)} className="p-2 text-gray-500 hover:text-blue-600" title="View Details"><EyeIcon /></button></td>
                            </tr>
                         )) : (
                            <tr><td colSpan="7" className="text-center py-10 text-gray-500">No past reports found.</td></tr>
                         )}
                    </tbody>
                </table>
            </div>
             {totalPages > 1 && (
                <div className="flex justify-between items-center mt-4">
                    <div className="flex items-center space-x-2 text-sm">
                        <span>Items per page:</span>
                        <select value={itemsPerPage} onChange={(e) => setItemsPerPage(Number(e.target.value))} className="border rounded-md p-1">
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                    <div className="flex items-center space-x-2">
                        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1 border rounded-md disabled:opacity-50">Prev</button>
                        <span>Page {currentPage} of {totalPages}</span>
                        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="px-3 py-1 border rounded-md disabled:opacity-50">Next</button>
                    </div>
                </div>
            )}
        </div>
    );
};

